// The service worker updates itself (vite.config.ts: autoUpdate, so a new one
// skips waiting and takes over open pages), but only once the browser checks
// for one. It checks on a cold start, not when an iPhone home-screen app
// comes back from the background, so a phone could run an old build for days
// (architecture review 2026-10-25, W4).

// UPDATE_READY is dispatched on window when a new build has taken over.
export const UPDATE_READY = 'harmovi:update-ready'

// CHECK_EVERY_MS spaces the checks, so flicking between apps asks once.
const CHECK_EVERY_MS = 60_000

interface Listens {
  addEventListener(type: string, listener: () => void): void
}

export interface UpdateWatch {
  container: Listens & { controller: unknown }
  doc: Listens & { visibilityState: string }
  registration: Promise<{ update(): Promise<unknown> }>
  onUpdate: () => void
  now?: () => number
}

// watchForUpdates asks the browser for a newer service worker each time the
// app comes back to the foreground, and calls onUpdate once a new build has
// taken over a page an older one served. Until the page reloads it calls
// onUpdate again on every return to the app: a home-screen app may never be
// restarted, so a missed offer would leave it on the old code. The first
// worker taking over a fresh install is not an update.
export function watchForUpdates({ container, doc, registration, onUpdate, now = Date.now }: UpdateWatch): void {
  registration.catch(() => {
    // No service worker: nothing to update.
  })
  let controlled = container.controller != null
  let updated = false
  container.addEventListener('controllerchange', () => {
    if (controlled) {
      updated = true
      onUpdate()
    }
    controlled = true
  })
  let checked = -Infinity
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState !== 'visible') return
    if (updated) {
      onUpdate()
      return
    }
    if (now() - checked < CHECK_EVERY_MS) return
    checked = now()
    registration
      .then((r) => r.update())
      .catch(() => {
        // Offline, or the check failed: the next return to the app tries again.
      })
  })
}
